import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";

const CURATED_PALETTE = [
  { top: 'rgba(99, 102, 241, 0.88)', bottom: 'rgba(99, 102, 241, 0.38)', hoverTop: 'rgba(99, 102, 241, 1)', hoverBottom: 'rgba(99, 102, 241, 0.65)' },
  { top: 'rgba(16, 185, 129, 0.88)', bottom: 'rgba(16, 185, 129, 0.38)', hoverTop: 'rgba(16, 185, 129, 1)', hoverBottom: 'rgba(16, 185, 129, 0.65)' },
  { top: 'rgba(245, 158, 11, 0.88)', bottom: 'rgba(245, 158, 11, 0.38)', hoverTop: 'rgba(245, 158, 11, 1)', hoverBottom: 'rgba(245, 158, 11, 0.65)' },
  { top: 'rgba(239, 68, 68, 0.88)', bottom: 'rgba(239, 68, 68, 0.38)', hoverTop: 'rgba(239, 68, 68, 1)', hoverBottom: 'rgba(239, 68, 68, 0.65)' },
  { top: 'rgba(139, 92, 246, 0.88)', bottom: 'rgba(139, 92, 246, 0.38)', hoverTop: 'rgba(139, 92, 246, 1)', hoverBottom: 'rgba(139, 92, 246, 0.65)' },
  { top: 'rgba(6, 182, 212, 0.88)', bottom: 'rgba(6, 182, 212, 0.38)', hoverTop: 'rgba(6, 182, 212, 1)', hoverBottom: 'rgba(6, 182, 212, 0.65)' },
  { top: 'rgba(236, 72, 153, 0.88)', bottom: 'rgba(236, 72, 153, 0.38)', hoverTop: 'rgba(236, 72, 153, 1)', hoverBottom: 'rgba(236, 72, 153, 0.65)' },
  { top: 'rgba(20, 184, 166, 0.88)', bottom: 'rgba(20, 184, 166, 0.38)', hoverTop: 'rgba(20, 184, 166, 1)', hoverBottom: 'rgba(20, 184, 166, 0.65)' },
];

// Accepts #rgb, #rrggbb or #rrggbbaa. Any alpha in the source is dropped: the
// gradient below supplies its own, so bars stay legible against the grid.
const hexToRgb = (hex) => {
  let h = String(hex || "").replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length < 6) return null;
  const int = parseInt(h.slice(0, 6), 16);
  if (Number.isNaN(int)) return null;
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
};

const paletteFromHex = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const { r, g, b } = rgb;
  return {
    top: `rgba(${r}, ${g}, ${b}, 0.88)`,
    bottom: `rgba(${r}, ${g}, ${b}, 0.38)`,
    hoverTop: `rgba(${r}, ${g}, ${b}, 1)`,
    hoverBottom: `rgba(${r}, ${g}, ${b}, 0.65)`,
  };
};

const chartOptions = {
  maintainAspectRatio: false,
  legend: {
    position: 'bottom',
    labels: {
      usePointStyle: true,
      padding: 24,
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
      fontSize: 12,
      fontColor: '#64748b',
      boxWidth: 8,
    },
  },
  tooltips: {
    backgroundColor: '#0f172a',
    titleFontFamily: "'Inter', 'Segoe UI', sans-serif",
    titleFontSize: 13,
    titleFontColor: '#f8fafc',
    bodyFontFamily: "'Inter', 'Segoe UI', sans-serif",
    bodyFontSize: 12,
    bodyFontColor: '#cbd5e1',
    cornerRadius: 12,
    xPadding: 16,
    yPadding: 14,
    displayColors: true,
    mode: 'index',
    intersect: false,
    borderColor: 'rgba(255,255,255,0.08)',
    borderWidth: 1,
    caretSize: 6,
  },
  scales: {
    xAxes: [{
      gridLines: {
        display: false,
      },
      ticks: {
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        fontSize: 11,
        fontColor: '#94a3b8',
        padding: 10,
      },
      barPercentage: 0.62,
      categoryPercentage: 0.72,
    }],
    yAxes: [{
      gridLines: {
        color: 'rgba(148, 163, 184, 0.05)',
        drawBorder: false,
        zeroLineColor: 'rgba(148, 163, 184, 0.08)',
      },
      ticks: {
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        fontSize: 11,
        fontColor: '#94a3b8',
        beginAtZero: true,
        padding: 12,
      },
    }],
  },
  hover: {
    mode: 'index',
    intersect: false,
  },
};

// colorFor: optional (seriesName) => hex. Series it returns nothing for fall
// back to the curated palette, as does every chart that omits the prop.
const BarChart = ({ graphData = [], colorFor }) => {
  const data = (canvas) => {
    if (!graphData || graphData.length === 0) {
      return { labels: [], datasets: [] };
    }

    const firstItem = graphData[0];
    const labels = [...(firstItem?.label || [])];
    const ctx = canvas.getContext("2d");

    const datasets = graphData.map((item, idx) => {
      const palette =
        (colorFor && paletteFromHex(colorFor(item?.type))) ||
        CURATED_PALETTE[idx % CURATED_PALETTE.length];

      // Create modern visual gradient for each bar
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height || 260);
      gradient.addColorStop(0, palette.top);
      gradient.addColorStop(1, palette.bottom);

      const hoverGradient = ctx.createLinearGradient(0, 0, 0, canvas.height || 260);
      hoverGradient.addColorStop(0, palette.hoverTop);
      hoverGradient.addColorStop(1, palette.hoverBottom);

      return {
        label: item?.type,
        backgroundColor: gradient,
        borderColor: 'transparent',
        borderWidth: 0,
        hoverBackgroundColor: hoverGradient,
        hoverBorderColor: 'transparent',
        data: [...(item?.data || [])],
      };
    });

    return { labels, datasets };
  };

  return (
    <div className="chart">
      <Bar
        data={data}
        width={100}
        height={50}
        options={chartOptions}
      />
    </div>
  );
};


export default BarChart;
